import React, {useState} from 'react'
import {Form} from "semantic-ui-react";
import {DateInput} from 'semantic-ui-calendar-react-yz'
import {displayDateFormat, isValidDate} from '../../utils/date'
import {convertDisplayDateFormatToServer, formatPartialDate, parseDate, serverDateFormat} from "../../utils/common";
import moment from "moment";
import PropTypes from 'prop-types';

const INVALID_DATE = "Invalid date";
const MAX_DOB_LENGTH = 10;
const DATE_REGEX = /^[0-9/]+$/

const isValidText = (e, value, isMasked) => {
    // when user selects date from keyboard or clicking on calendar
    if ((!e || !e.nativeEvent) && value.length === MAX_DOB_LENGTH) {
        return true;
    }
    // is pasting date and length is MAX_DOB_LENGTH
    if (e.nativeEvent.inputType === "insertFromPaste" && value.length === MAX_DOB_LENGTH) {
        return true;
    } else
        // is typing date only with numbers
    if (e.nativeEvent.inputType === "insertText" && !isMasked && !isNaN(value) && value.length < MAX_DOB_LENGTH && value.length > 0) {
        return true;
    } else
        // is removing characters (when input it's visible or user selected the whole date)
    if (e.nativeEvent.inputType === "deleteContentBackward") {
        if ((value.length > 0 && !isMasked) || value.length === 0) {
            return true;
        }
    } else
        // allow typing characters with no masking or date is picked from calendar
    if (((e.nativeEvent.inputType === "insertText" && !isMasked) || (e.nativeEvent.type === "click")) && (value.length === MAX_DOB_LENGTH || DATE_REGEX.test(value))) {
        return true;
    }
    return false;
}

export const DateSelector = ({
                                 onChange,
                                 onFocus = () => {},
                                 onBlur = () => {},
                                 setFormError = () => {},
                                 iconPosition = "right",
                                 minDate = null,
                                 maxDate = null,
                                 required = false,
                                 icon = false,
                                 inputClassName = "",
                                 isReadonly = false,
                                 duration = 0,
                                 isMasked = false,
                                 value,
                                 width,
                                 ...props
                             }) => {

    const [inputValue, setInputvalue] = useState(value);
    const [error, setError] = useState('');

    const handleBlur = () => {
        const isEmpty = !value || value.length === 0;
        if (isEmpty) {
            if (!!required) {
                setError(INVALID_DATE);
                setFormError(INVALID_DATE);
            }
        } else {
            const rawValue = isMasked ? inputValue : value;
            const parts = rawValue.split("/");
            let errorMessage;

            if (parts.length !== 3) {
                errorMessage = INVALID_DATE;
                setError(errorMessage);
                setFormError(errorMessage);
                return;
            }

            const [mm, dd, yyyy] = parts;
            const requiredDateLength = rawValue.length === 10;
            if (!mm || !dd || !yyyy || !requiredDateLength) {
                errorMessage = `Format should be ${displayDateFormat}`;
                setError(errorMessage);
                setFormError(errorMessage);
                return;
            }

            const strict = parseDate(rawValue);
            if (!strict.isValid()) {
                errorMessage = INVALID_DATE;
                setError(errorMessage);
                setFormError(errorMessage);
                return;
            }

            if (minDate) {
                const min = moment(minDate);
                if (strict.isBefore(min, 'day')) {
                    errorMessage = 'Please enter a valid year.';
                    setError(errorMessage);
                    setFormError(errorMessage);
                    return;
                }
            }

            const serverFormat = convertDisplayDateFormatToServer(rawValue);
            const valid = isValidDate({ value: serverFormat, format: serverDateFormat });
            if (!valid) {
                errorMessage = INVALID_DATE;
                setError(errorMessage);
                setFormError(errorMessage);
                return;
            }

            setError('');
            setFormError(null);
        }

        if (!!onBlur) {
            onBlur();
        }
    };

    const handleChange = (event, {value}) => {
        if (!isValidText(event, value, isMasked)) {
            return;
        }
        if (!isMasked && value.length > 1) {
            const lastChar = value[value.length - 1]
            const isDeletingCharacters = value.length < inputValue.length;
            if (!(lastChar === "/" && isDeletingCharacters)) {
                const reg = /^\d*$/;
                if (!reg.test(lastChar)) {
                    return;
                }
            }
        }
        const date = formatPartialDate(value, inputValue);
        setError('');
        setFormError(null);
        onChange(event, {value: date});
        setInputvalue(date);
    };

    return (
        <Form.Field width={width}>
            <DateInput
                closable={true}
                readOnly={isReadonly}
                duration={duration}
                animation={""}
                startMode={"month"}
                error={!!error}
                dateFormat={displayDateFormat}
                placeholder={displayDateFormat}
                minDate={minDate}
                maxDate={maxDate}
                required={required}
                onFocus={() => {
                    setError('');
                    setFormError(null);
                    if (!!onFocus) {
                        onFocus();
                    }
                }}
                onBlur={handleBlur}
                onChange={handleChange}
                iconPosition={iconPosition}
                value={value}
                icon={icon}
                hideMobileKeyboard={true}
                {...props}
            />
            {error?.length > 0 && (
                <small className={"warningRedText"}>{error}</small>
            )}
        </Form.Field>
    )
};

DateSelector.propTypes = {
    onChange: (props, propName, componentName) => {
        if (!props.isReadonly && (props[propName] === undefined || typeof props[propName] !== 'function')) {
            return new Error(`Invalid prop ${propName} supplied to ${componentName}. When isReadonly is false, ${propName} is required and must be a function.`);
        }
    },
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    setFormError: PropTypes.func,
    icon: PropTypes.string,
    iconPosition: PropTypes.string,
    isMasked: PropTypes.bool,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    required: PropTypes.bool,
    isReadonly: PropTypes.bool,
    duration: PropTypes.number,
    value: PropTypes.string,
    width: PropTypes.number,
};
